import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";

function sampleVars() {
  const d = new Date();
  d.setDate(d.getDate() + 2);
  d.setHours(9, 0, 0, 0);

  const formatted = d
    .toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace(/\u202f/g, " ");

  return {
    lead_name: "123 Ford",
    activity_type: "Meeting",
    next_contact_date: formatted,
    notes: "Confirm demo slot",
    rep_name: "John Doe",
    link: `${window.location.origin}/leads/123`,
  };
}

function renderTemplate(template, vars) {
  if (!template) return "";

  /* First, resolve {{#if var}}...{{/if}} blocks */
  let resolved = String(template).replace(
    /\{\{#if\s+([a-zA-Z0-9_]+)\s*\}\}([\s\S]*?)\{\{\/if\}\}/g,
    (_, key, inner) => {
      const value = vars[key];
      return value && String(value).trim()
        ? inner.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (__, k) =>
            Object.prototype.hasOwnProperty.call(vars, k)
              ? String(vars[k] ?? "")
              : "",
          )
        : "";
    },
  );

  /* Then, resolve plain placeholders */
  resolved = resolved.replace(
    /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g,
    (_, key) =>
      Object.prototype.hasOwnProperty.call(vars, key)
        ? String(vars[key] ?? "")
        : "",
  );

  return resolved;
}

function EmailTemplatePreview({
  subject,
  body,
  onClose,
  onSendTest,
  sending = false,
  sendResult = null,
}) {
  const [width, setWidth] = useState("desktop");
  const vars = useMemo(sampleVars, []);
  const renderedSubject = renderTemplate(subject, vars);
  const renderedBody = renderTemplate(body, vars);

  const frameClass =
    width === "mobile"
      ? "email-preview-frame email-preview-frame-mobile"
      : "email-preview-frame email-preview-frame-desktop";

  return createPortal(
    <div
      className="modal-overlay email-preview-overlay"
      role="presentation"
      onMouseDown={() => !sending && onClose()}
    >
      <div
        className="modal email-preview-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Email preview"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="email-preview-modal-header">
          <div>
            <h3>Email Preview</h3>
            <p className="text-muted" style={{ margin: 0, fontSize: 12 }}>
              Rendered with sample data. Real emails use live values.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            disabled={sending}
            aria-label="Close"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Email-client header */}
        <div className="email-preview-client-header">
          <div className="email-preview-client-row">
            <span className="email-preview-client-label">From:</span>
            <span className="email-preview-client-value">
              IVSG CRM &lt;system@ivsgllc.com&gt;
            </span>
          </div>
          <div className="email-preview-client-row">
            <span className="email-preview-client-label">To:</span>
            <span className="email-preview-client-value">
              John Doe &lt;john.doe@example.com&gt;
            </span>
          </div>
          <div className="email-preview-client-row">
            <span className="email-preview-client-label">Subject:</span>
            <span className="email-preview-client-value email-preview-client-subject">
              {renderedSubject || "(no subject)"}
            </span>
          </div>
        </div>

        {/* Width toggle */}
        <div className="email-preview-toolbar">
          <div className="email-preview-width-toggle">
            <button
              type="button"
              className={width === "desktop" ? "active" : ""}
              onClick={() => setWidth("desktop")}
            >
              <i className="fas fa-desktop" /> Desktop
            </button>
            <button
              type="button"
              className={width === "mobile" ? "active" : ""}
              onClick={() => setWidth("mobile")}
            >
              <i className="fas fa-mobile-alt" /> Mobile
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="email-preview-modal-body">
          <div
            className={frameClass}
            dangerouslySetInnerHTML={{ __html: renderedBody || "" }}
          />

          {sendResult?.error && (
            <div className="error-message" style={{ marginTop: 12 }}>
              <i className="fas fa-exclamation-circle" /> {sendResult.error}
            </div>
          )}
          {sendResult?.ok && (
            <div className="email-preview-send-ok">
              <i className="fas fa-check-circle" /> Test email sent to your
              address.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="email-preview-modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={sending}
          >
            Close
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onSendTest}
            disabled={sending}
          >
            {sending ? (
              <>
                <i className="fas fa-spinner fa-spin" /> Sending...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane" /> Send test to myself
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default EmailTemplatePreview;